import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  deleteCommerceMaterialItem,
  getCommerceMaterials,
  patchCommerceMaterialItem,
} from '../../../../services/commerceService.ts';
import {
  buildCommerceMaterialRowFromManualMaterialSubmitPayload,
  parseSheetConfirmResponse,
  postCommerceMaterialItemCreate,
  type ManualMaterialSubmitPayload,
} from '../../../../services/materialCommerceSheetService.ts';
import { normalizeUserCommerces } from '../../../../utils/userCommerces.ts';
import CommerceMaterialsCatalog from './CommerceMaterialsCatalog.tsx';
import {
  extractCommerceMaterialsDataArray,
  flattenCategories,
  parseCommerceMaterialsPayload,
} from './commerceMaterialsModel.ts';
import styles from './MisMaterialesComercio.module.scss';

interface MisMaterialesComercioProps {
  userData: Record<string, unknown> | null | undefined;
  setInfoReady: (ready: boolean) => void;
  surface?: 'gestion' | 'route';
}

const MisMaterialesComercio: React.FC<MisMaterialesComercioProps> = ({
  userData,
  setInfoReady,
  surface = 'gestion',
}) => {
  const commerces = useMemo(() => normalizeUserCommerces(userData), [userData]);

  const initialCommerceId = useMemo(() => (commerces[0]?.id != null ? commerces[0].id : null), [commerces]);

  const [selectedCommerceId, setSelectedCommerceId] = useState<number | null>(initialCommerceId);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<unknown[] | null>(null);
  const [rawFallback, setRawFallback] = useState<string | null>(null);

  useEffect(() => {
    setSelectedCommerceId(initialCommerceId);
  }, [initialCommerceId]);

  const loadMaterials = useCallback(
    async (commerceId: number, opts?: { preserveUi?: boolean }) => {
      const preserveUi = Boolean(opts?.preserveUi);
      if (!preserveUi) {
        setLoading(true);
        setPayload(null);
      }
      setError(null);
      setRawFallback(null);
      try {
        const res = await getCommerceMaterials(commerceId);
        const rawArr = extractCommerceMaterialsDataArray(res.data);
        if (rawArr == null) {
          setPayload([]);
          setRawFallback(JSON.stringify(res.data, null, 2));
        } else {
          setPayload(rawArr as unknown[]);
        }
      } catch (e: unknown) {
        const msg = isAxiosError(e)
          ? (typeof e.response?.data === 'object' &&
              e.response?.data != null &&
              'message' in (e.response.data as object) &&
              String((e.response.data as { message?: string }).message)) ||
            e.message
          : e instanceof Error
            ? e.message
            : 'No se pudieron cargar los materiales.';
        setError(msg || 'No se pudieron cargar los materiales.');
      } finally {
        setLoading(false);
        setInfoReady(true);
      }
    },
    [setInfoReady]
  );

  useEffect(() => {
    if (selectedCommerceId == null) {
      setInfoReady(true);
      return;
    }
    void loadMaterials(selectedCommerceId);
  }, [loadMaterials, selectedCommerceId, setInfoReady]);

  const flatRows = useMemo(() => {
    if (!payload || selectedCommerceId == null) return [];
    const categories = parseCommerceMaterialsPayload(payload, selectedCommerceId);
    return flattenCategories(categories);
  }, [payload, selectedCommerceId]);

  const handleEditItem = useCallback(
    async (
      itemId: number,
      patch: {
        price: number | null;
        unquoted: boolean;
        valueAddedTax: boolean;
        promotion: string;
        promotionDiscount: number | null;
      }
    ) => {
      if (selectedCommerceId == null) return;
      setMutating(true);
      try {
        await patchCommerceMaterialItem(selectedCommerceId, itemId, {
          price: patch.price ?? undefined,
          unquoted: patch.unquoted,
          valueAddedTax: patch.unquoted ? false : patch.valueAddedTax,
          promotion: patch.promotion.trim() ? patch.promotion.trim() : null,
          promotionDiscount: patch.promotionDiscount,
        });
        await loadMaterials(selectedCommerceId, { preserveUi: true });
      } finally {
        setMutating(false);
      }
    },
    [loadMaterials, selectedCommerceId]
  );

  const handleDeleteItem = useCallback(
    async (itemId: number) => {
      if (selectedCommerceId == null) return;
      setMutating(true);
      try {
        await deleteCommerceMaterialItem(selectedCommerceId, itemId);
        await loadMaterials(selectedCommerceId, { preserveUi: true });
      } finally {
        setMutating(false);
      }
    },
    [loadMaterials, selectedCommerceId]
  );

  const handleAddItem = useCallback(
    async (payload: ManualMaterialSubmitPayload): Promise<{ ok: boolean; message?: string }> => {
      if (selectedCommerceId == null) {
        return { ok: false, message: 'No hay comercio seleccionado.' };
      }
      setMutating(true);
      try {
        let row: Record<string, unknown>;
        try {
          row = buildCommerceMaterialRowFromManualMaterialSubmitPayload(payload);
        } catch (e: unknown) {
          return { ok: false, message: e instanceof Error ? e.message : 'No se pudo preparar el alta.' };
        }
        const res = await postCommerceMaterialItemCreate(selectedCommerceId, { rows: [row] });
        const parsed = parseSheetConfirmResponse(res.data);
        const r0 = parsed?.results?.[0];
        if (r0 && r0.outcome.trim().toLowerCase() === 'failed') {
          return { ok: false, message: r0.reason || 'No se pudo registrar el material.' };
        }
        if (parsed?.failed?.length) {
          return { ok: false, message: 'No se pudo registrar el material.' };
        }
        await loadMaterials(selectedCommerceId, { preserveUi: true });
        return { ok: true };
      } catch (e: unknown) {
        if (e instanceof Error && e.message) {
          return { ok: false, message: e.message };
        }
        if (isAxiosError(e)) {
          const msg =
            (typeof e.response?.data === 'object' &&
              e.response?.data &&
              'message' in e.response.data &&
              String((e.response.data as { message?: string }).message)) ||
            e.message;
          return { ok: false, message: msg || 'No pudimos registrar el material.' };
        }
        return { ok: false, message: 'No pudimos registrar el material.' };
      } finally {
        setMutating(false);
      }
    },
    [loadMaterials, selectedCommerceId]
  );

  if (!commerces.length) {
    return (
      <div className={styles.wrap}>
        <p className={styles.intro}>
          No tenés un comercio registrado todavía. Cuando lo des de alta y cargues un listado de precios, vas a poder ver
          acá los productos de tu comercio.
        </p>
        <Link className={styles.ctaLink} to="/comercios/alta">
          Dar de alta mi comercio
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {surface === 'gestion' ? (
        <div className={styles.header}>
          <h2 className={styles.title}>Catálogo de materiales</h2>
          <p className={styles.intro}>
            Listado por categorías del catálogo con los precios y condiciones que cargaste. Podés filtrar y buscar; la
            tabla se pagina para listas grandes.
          </p>
        </div>
      ) : null}
      {loading ? <p className={styles.loading}>Cargando materiales…</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      {!loading && !error && payload && flatRows.length === 0 && !rawFallback ? (
        <p className={styles.empty}>Todavía no hay materiales asociados a este comercio.</p>
      ) : null}
      {!loading && !error && flatRows.length > 0 ? (
        <CommerceMaterialsCatalog
          mode="owner"
          flatRows={flatRows}
          disableActions={mutating}
          onEditItem={handleEditItem}
          onDeleteItem={handleDeleteItem}
          onAddItem={surface === 'gestion' ? handleAddItem : undefined}
          fileImportHref={
            surface === 'route' && selectedCommerceId != null
              ? `/comercios/lista-precios?commerceId=${selectedCommerceId}`
              : undefined
          }
        />
      ) : null}
      {!loading && !error && rawFallback ? (
        <>
          <p className={styles.fallback}>Respuesta del servidor (formato no reconocido):</p>
          <pre className={styles.pre}>{rawFallback}</pre>
        </>
      ) : null}
    </div>
  );
};

export default MisMaterialesComercio;
